import React, { useEffect, useState, useCallback } from "react";
import { fetchAllUsersWithPagination } from "../../services/adminUserService";
import { useNavigate } from "react-router-dom";
import UserSearchFilters from "./UserSearchFilters";
import UserTablePagination from "./UserTablePagination";
import WhatsAppMessageModal from "./WhatsAppMessageModal";
import RegisterUserModal from "./RegisterUserModal";
import DeleteUserModal from "./DeleteUserModal";

const EnhancedAdminUsersTable = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 0,
    totalPages: 0,
    totalElements: 0,
    pageSize: 20
  });
  const [filters, setFilters] = useState({
    search: "",
    sortBy: "lastLogin",
    sortDir: "desc"
  });
  const [selectedUser, setSelectedUser] = useState(null);
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [toast, setToast] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);

  const navigate = useNavigate();

  const loadUsers = useCallback(async (page = 0, newFilters = filters, pageSize = pagination.pageSize) => {
    setLoading(true);
    try {
      const response = await fetchAllUsersWithPagination({
        page,
        size: pageSize,
        ...newFilters
      });
      setUsers(response.data.data);
      setPagination({
        currentPage: response.data.currentPage,
        totalPages: response.data.totalPages,
        totalElements: response.data.totalElements,
        pageSize: response.data.pageSize
      });
    } catch (error) {
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUsers();
  }, []);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(""), 4000);
  };

  const handleRegisterSuccess = () => {
    setShowRegisterModal(false);
    showToast("User registered successfully. Login credentials sent to their email.");
    loadUsers(0, filters);
  };

  const handleSearch = (searchFilters) => {
    setFilters(searchFilters);
    loadUsers(0, searchFilters);
  };

  const handlePageChange = (page, newPageSize = null) => {
    if (newPageSize && newPageSize !== pagination.pageSize) {
      setPagination(prev => ({ ...prev, pageSize: newPageSize }));
      loadUsers(0, filters, newPageSize);
    } else {
      loadUsers(page);
    }
  };

  const handleShowMore = (userId) => {
    navigate(`/admin/users/${userId}`);
  };

  const handleSendWhatsApp = (user) => {
    setSelectedUser(user);
    setShowWhatsAppModal(true);
  };

  const handleDeleteSuccess = (deletedUser) => {
    setDeleteTarget(null);
    showToast(`User ${deletedUser.name || deletedUser.email} has been deleted successfully`);
    loadUsers(pagination.currentPage, filters);
  };

  const formatWhatsAppNumber = (user) => {
    if (user.countryCode && user.mobileNumber) return `${user.countryCode} ${user.mobileNumber}`;
    if (user.whatsappNumber) return user.whatsappNumber;
    return "Not provided";
  };

  if (loading && users.length === 0) {
    return <div className="loading">Loading users...</div>;
  }

  return (
    <div className="enhanced-users-table">
      {toast && <div className="reg-toast">{toast}</div>}

      <div className="users-toolbar">
        <UserSearchFilters onSearch={handleSearch} filters={filters} />
        <button className="btn-register-user" onClick={() => setShowRegisterModal(true)}>
          ➕ Register User
        </button>
      </div>

      <div className="table-stats">
        <span>Total Users: {pagination.totalElements}</span>
        <span>WhatsApp Enabled: {users.filter(u => u.whatsappNumber).length}</span>
        <span>Consented: {users.filter(u => u.whatsappConsent).length}</span>
      </div>

      <div className="table-container">
        <table className="users-table">
          <thead>
            <tr>
              <th>Picture</th>
              <th>Name</th>
              <th>Email</th>
              <th>WhatsApp</th>
              <th>Consent</th>
              <th>Last Login</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user.id}>
                <td>
                  {user.picture ? (
                    <img src={user.picture} alt="avatar" className="user-avatar" />
                  ) : (
                    <div className="avatar-placeholder">N/A</div>
                  )}
                </td>
                <td>{user.name || "N/A"}</td>
                <td>{user.email}</td>
                <td className="whatsapp-number">
                  {formatWhatsAppNumber(user)}
                </td>
                <td>
                  <span className={`consent-badge ${user.whatsappConsent ? 'consented' : 'not-consented'}`}>
                    {user.whatsappConsent ? "✅ Yes" : "❌ No"}
                  </span>
                </td>
                <td>
                  {user.lastLogin
                    ? new Date(user.lastLogin).toLocaleString()
                    : "Never"}
                </td>
                <td>
                  <span className={`status-badge ${user.suspended ? 'suspended' : 'active'}`}>
                    {user.suspended ? "Suspended" : "Active"}
                  </span>
                </td>
                <td className="actions">
                  <button
                    className="btn-primary"
                    onClick={() => handleShowMore(user.id)}
                  >
                    Details
                  </button>
                  {user.whatsappNumber && user.whatsappConsent && (
                    <button
                      className="btn-whatsapp"
                      onClick={() => handleSendWhatsApp(user)}
                    >
                      📱 WhatsApp
                    </button>
                  )}
                  <button
                    style={{ background: "#c0392b", color: "#fff", border: "none", padding: "6px 12px", borderRadius: "4px", cursor: "pointer", fontSize: "13px" }}
                    onClick={() => setDeleteTarget(user)}
                  >
                    🗑 Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <UserTablePagination
        pagination={pagination}
        onPageChange={handlePageChange}
        loading={loading}
      />

      {showWhatsAppModal && (
        <WhatsAppMessageModal
          user={selectedUser}
          onClose={() => setShowWhatsAppModal(false)}
        />
      )}

      {showRegisterModal && (
        <RegisterUserModal
          onClose={() => setShowRegisterModal(false)}
          onSuccess={handleRegisterSuccess}
        />
      )}

      {deleteTarget && (
        <DeleteUserModal
          user={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onSuccess={handleDeleteSuccess}
        />
      )}
    </div>
  );
};

export default EnhancedAdminUsersTable;
